import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { createSignature, getSignatures, updateSignature } from "../api/signaturesApi";

async function imageHasTransparency(file) {
  return new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.width;
        canvas.height = image.height;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        context.drawImage(image, 0, 0);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let hasTransparency = false;
        for (let index = 3; index < pixels.length; index += 4) {
          if (pixels[index] < 255) {
            hasTransparency = true;
            break;
          }
        }
        URL.revokeObjectURL(imageUrl);
        resolve(hasTransparency);
      } catch (error) {
        URL.revokeObjectURL(imageUrl);
        reject(error);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("Failed to read signature image."));
    };
    image.src = imageUrl;
  });
}

const emptyForm = {
  name: "",
  title: "",
  signatureImage: null,
  isActive: true,
};

export default function SignaturesSettingsPage() {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState(emptyForm);
  const [fileLabel, setFileLabel] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const resetForm = () => {
    setFormValues(emptyForm);
    setFileLabel("");
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getSignatures({ includeInactive: true });
        if (!active) return;
        setSignatures(data);
      } catch (loadError) {
        console.error("Failed to load signatures:", loadError);
        if (active) {
          setError("Failed to load signatures.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const handleDialogClose = () => {
    if (saving) return;
    setDialogOpen(false);
    resetForm();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "image/png") {
      setToast({
        open: true,
        message: "Signature image must be a PNG.",
        severity: "error",
      });
      event.target.value = "";
      return;
    }

    try {
      const hasTransparency = await imageHasTransparency(file);
      if (!hasTransparency) {
        setToast({
          open: true,
          message: "Please upload a PNG with a transparent background.",
          severity: "error",
        });
        event.target.value = "";
        return;
      }
      setFormValues((prev) => ({ ...prev, signatureImage: file }));
      setFileLabel(file.name);
    } catch (uploadError) {
      console.error("Failed to validate signature image:", uploadError);
      setToast({
        open: true,
        message: "Failed to validate the signature image.",
        severity: "error",
      });
      event.target.value = "";
    }
  };

  const handleCreateSignature = async () => {
    if (!formValues.name.trim() || !formValues.title.trim() || !formValues.signatureImage) {
      setToast({
        open: true,
        message: "Name, title, and signature image are required.",
        severity: "error",
      });
      return;
    }

    try {
      setSaving(true);
      const createdSignature = await createSignature({
        name: formValues.name.trim(),
        title: formValues.title.trim(),
        signatureImage: formValues.signatureImage,
        isActive: formValues.isActive,
      });
      setSignatures((prev) =>
        [...prev, createdSignature].sort((left, right) => {
          if (left.is_active !== right.is_active) {
            return left.is_active ? -1 : 1;
          }
          return left.name.localeCompare(right.name);
        })
      );
      setToast({
        open: true,
        message: "Signature added.",
        severity: "success",
      });
      setDialogOpen(false);
      resetForm();
    } catch (saveError) {
      console.error("Failed to create signature:", saveError);
      setToast({
        open: true,
        message: saveError.response?.data?.error || "Failed to add signature.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (signature) => {
    try {
      const updated = await updateSignature(signature.id, {
        isActive: !signature.is_active,
      });
      setSignatures((prev) =>
        prev
          .map((item) => (item.id === signature.id ? updated : item))
          .sort((left, right) => {
            if (left.is_active !== right.is_active) {
              return left.is_active ? -1 : 1;
            }
            return left.name.localeCompare(right.name);
          })
      );
    } catch (updateError) {
      console.error("Failed to update signature:", updateError);
      setToast({
        open: true,
        message: updateError.response?.data?.error || "Failed to update signature.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Signatures
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          + Add Signature
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Preview</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && signatures.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No signatures added yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              signatures.map((signature) => (
                <TableRow key={signature.id}>
                  <TableCell>{signature.name}</TableCell>
                  <TableCell>{signature.title}</TableCell>
                  <TableCell>
                    {signature.signature_image_data_url ? (
                      <Box
                        component="img"
                        src={signature.signature_image_data_url}
                        alt={`${signature.name} signature`}
                        sx={{ maxHeight: 48, maxWidth: 180, display: "block" }}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(signature.is_active)}
                          onChange={() => handleToggleActive(signature)}
                        />
                      }
                      label={
                        <Chip
                          size="small"
                          label={signature.is_active ? "Active" : "Inactive"}
                          color={signature.is_active ? "success" : "default"}
                        />
                      }
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Add Signature</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              value={formValues.name}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, name: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Title"
              value={formValues.title}
              onChange={(event) =>
                setFormValues((prev) => ({ ...prev, title: event.target.value }))
              }
              fullWidth
            />
            <Box>
              <Button component="label" variant="outlined">
                Upload Signature PNG
                <input hidden type="file" accept="image/png" onChange={handleFileChange} />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Upload a PNG image with a transparent background.
              </Typography>
              {fileLabel && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {fileLabel}
                </Typography>
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.isActive}
                  onChange={(event) =>
                    setFormValues((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleCreateSignature} variant="contained" disabled={saving}>
            Add Signature
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
